import { parsePhoneValue } from './parsePhoneValue';

/**
 * Validate a phone number regardless of the format it is stored/typed in.
 *
 * Historically callers checked the raw string length (e.g. `phone.length < 13`),
 * which only accepts the exact "+1 8133446984" shape (country code, space, 10
 * digits). Values stored in any of the other formats we've seen in the wild
 * ("+18133446984", "8133446984", ...) display fine via parsePhoneValue but fail
 * that brittle check — the "Phone number must be 10 digits" bug where a valid
 * 10-digit number is rejected on save. This validates the parsed national number
 * instead, so every storage format that represents a real 10-digit number passes.
 *
 * Returns an error message string, or false when the number is valid (or empty —
 * callers decide whether a phone is required).
 *
 * US/Canada (+1) numbers require at least 10 national digits. Other country codes
 * are left unvalidated here, matching the prior behavior (the old check only ever
 * fired for +1 numbers).
 */
export const validatePhoneNumber = (
  value: string | null | undefined,
  defaultCountryCode = '+1'
): string | false => {
  if (!value) return false;

  const { countryCode, nationalNumber } = parsePhoneValue(value, defaultCountryCode);

  if (countryCode === '+1' && nationalNumber.length < 10) {
    return 'Phone number must be 10 digits.';
  }

  return false;
};
